import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { KioskBrand } from "./components/KioskBrand";
import { IdleTimer } from "./components/IdleTimer";
import {
  clearCredentials,
  isProvisioned,
  isRefreshTokenValid,
  refreshAccessToken,
  startBackgroundTasks,
} from "./lib/device-auth";
import { KioskApiError } from "./lib/kiosk-client";
import { clearBootstrapCache, fetchBootstrap } from "./lib/bootstrap";
import type { BootstrapPayload } from "./lib/kiosk-client";
import { AdminPage } from "./pages/AdminPage";
import { FlowPage } from "./pages/FlowPage";
import { HandoffPage } from "./pages/HandoffPage";
import { IdlePage } from "./pages/IdlePage";
import { SetupPage } from "./pages/SetupPage";

type AppScreen = "loading" | "setup" | "idle" | "flow" | "handoff" | "admin";

export function App() {
  const location = useLocation();
  const [screen, setScreen] = useState<AppScreen>("loading");
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);

  const goIdle = useCallback(() => {
    setScreen("idle");
  }, []);

  // On mount: decide initial screen.
  useEffect(() => {
    async function init() {
      // Check for handoff QR code first.
      if (location.pathname === "/handoff") {
        if (!isProvisioned()) {
          setScreen("setup");
          return;
        }
      }

      if (!isProvisioned() || !isRefreshTokenValid()) {
        clearCredentials();
        setScreen("setup");
        return;
      }

      // Try to refresh access token silently, then fetch bootstrap.
      try {
        await refreshAccessToken();
        const data = await fetchBootstrap();
        setBootstrap(data);
        startBackgroundTasks();
        if (location.pathname === "/handoff") {
          setScreen("handoff");
        } else {
          setScreen("idle");
        }
      } catch (err) {
        if (err instanceof KioskApiError && err.status === 401) {
          clearCredentials();
          setScreen("setup");
        } else {
          // Network error — show idle with cached bootstrap if available.
          setScreen("setup");
        }
      }
    }
    void init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleProvisioned() {
    try {
      const data = await fetchBootstrap();
      setBootstrap(data);
      startBackgroundTasks();
      setScreen("idle");
    } catch {
      // Still go to idle; bootstrap will be retried on next navigation.
      setScreen("idle");
    }
  }

  // Secret admin tap: 5-finger tap on the top-right corner.
  // Implemented via a 60x60px invisible button in the corner.
  const [tapCount, setTapCount] = useState(0);
  const [tapTimer, setTapTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  function handleCornerTap() {
    const next = tapCount + 1;
    setTapCount(next);
    if (tapTimer) clearTimeout(tapTimer);
    if (next >= 5) {
      setTapCount(0);
      setShowAdmin(true);
    } else {
      setTapTimer(setTimeout(() => setTapCount(0), 2000));
    }
  }

  if (screen === "loading") {
    return (
      <div className="flex items-center justify-center h-full bg-[var(--kiosk-brand-bg)]">
        <div
          className="w-16 h-16 rounded-full border-8 border-[var(--kiosk-brand-accent)] border-t-transparent animate-spin"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  return (
    <>
      {bootstrap && <KioskBrand config={bootstrap.brand_config} />}

      {/* Idle timer: reset to idle after inactivity (only when in flow). */}
      {screen === "flow" && bootstrap && (
        <IdleTimer
          timeoutSeconds={bootstrap.idle_timeout_seconds}
          onTimeout={goIdle}
        />
      )}

      {/* Secret admin corner tap target */}
      <button
        onClick={handleCornerTap}
        className="fixed top-0 right-0 w-[60px] h-[60px] z-40 opacity-0"
        aria-hidden="true"
        tabIndex={-1}
      />

      {showAdmin && (
        <AdminPage
          onClose={() => setShowAdmin(false)}
          onRevoke={() => {
            setShowAdmin(false);
            clearBootstrapCache();
            setBootstrap(null);
            setScreen("setup");
          }}
        />
      )}

      <Routes>
        <Route
          path="/"
          element={
            screen === "setup" ? (
              <SetupPage onProvisioned={() => void handleProvisioned()} />
            ) : screen === "idle" && bootstrap ? (
              <IdlePage
                bootstrap={bootstrap}
                onStart={() => setScreen("flow")}
              />
            ) : screen === "flow" && bootstrap ? (
              <FlowPage bootstrap={bootstrap} onDone={goIdle} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div
                  className="w-16 h-16 rounded-full border-8 border-[var(--kiosk-brand-accent)] border-t-transparent animate-spin"
                  role="status"
                />
              </div>
            )
          }
        />
        <Route
          path="/handoff"
          element={
            screen === "setup" ? (
              <SetupPage onProvisioned={() => void handleProvisioned()} />
            ) : bootstrap ? (
              <HandoffPage bootstrap={bootstrap} onDone={goIdle} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
